import React, { useEffect, useRef, useState } from 'react';
import { FiCheck, FiMinus, FiPlus } from 'react-icons/fi';
import { FaCalculator, FaHandPointer, FaWeightScale } from 'react-icons/fa6';

const GestureStep = ({ icon, eyebrow, title, description, tone }) => (
  <div className={`rounded-2xl border p-4 ${tone}`}>
    <div className="flex items-start gap-3">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white text-lg shadow-sm ring-1 ring-black/5">
        {icon}
      </span>
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.16em] opacity-60">{eyebrow}</div>
        <h3 className="mt-0.5 text-sm font-black text-gray-900">{title}</h3>
        <p className="mt-1 text-xs leading-relaxed text-gray-600">{description}</p>
      </div>
    </div>
  </div>
);

const PosFavGestureGuide = ({
  isActive,
  preferencesReady,
  seen,
  onAcknowledge,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const presentedRef = useRef(false);

  useEffect(() => {
    if (!isActive) {
      setIsOpen(false);
      if (!seen) presentedRef.current = false;
      return;
    }
    if (!preferencesReady || seen || presentedRef.current) return;
    presentedRef.current = true;
    setSaveError('');
    setIsOpen(true);
  }, [isActive, preferencesReady, seen]);

  useEffect(() => {
    if (seen) setIsOpen(false);
  }, [seen]);

  if (!isOpen) return null;

  const handleAcknowledge = async () => {
    if (saving) return;
    setSaving(true);
    setSaveError('');
    try {
      await onAcknowledge?.();
      setIsOpen(false);
    } catch (error) {
      setSaveError(
        error?.response?.data?.message
        || error?.message
        || 'No pudimos guardar esta preferencia.'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[1700] flex items-center justify-center overflow-y-auto bg-slate-950/70 px-3 py-5 backdrop-blur-sm sm:px-6"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-favs-gesture-guide-title"
    >
      <div className="w-full max-w-3xl overflow-hidden rounded-[28px] border border-white/60 bg-white shadow-2xl">
        <div className="relative overflow-hidden bg-gradient-to-br from-blue-700 via-indigo-700 to-violet-700 px-5 py-6 text-white sm:px-8 sm:py-8">
          <div className="absolute -right-12 -top-16 h-44 w-44 rounded-full bg-white/10 blur-sm" />
          <div className="absolute -bottom-20 -left-12 h-40 w-40 rounded-full bg-cyan-300/20 blur-sm" />
          <div className="relative flex items-start gap-4">
            <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white/15 text-2xl shadow-inner ring-1 ring-white/20">
              <FaHandPointer aria-hidden="true" />
            </span>
            <div>
              <div className="text-[11px] font-black uppercase tracking-[0.2em] text-blue-100">
                Favs más rápidos
              </div>
              <h2 id="pos-favs-gesture-guide-title" className="mt-1 text-2xl font-black tracking-tight sm:text-3xl">
                Una tarjeta, tres gestos simples
              </h2>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-blue-100 sm:text-base">
                Cada Fav representa ahora una variedad con su precio exacto. Usa la mitad de la tarjeta según lo que quieras hacer.
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-5 sm:p-8">
          <div className="grid gap-3 sm:grid-cols-3">
            <GestureStep
              icon={<FiMinus className="text-red-600" aria-hidden="true" />}
              eyebrow="Mitad izquierda"
              title="Quitar"
              description="Un toque resta una unidad. Con la balanza conectada, quita el último pesaje de ese precio."
              tone="border-red-100 bg-red-50/70"
            />
            <GestureStep
              icon={<FiPlus className="text-emerald-600" aria-hidden="true" />}
              eyebrow="Mitad derecha"
              title="Agregar o confirmar"
              description="Un toque suma una unidad. Con la balanza conectada, confirma la lectura visible y la agrega al carrito."
              tone="border-emerald-100 bg-emerald-50/70"
            />
            <GestureStep
              icon={<FaCalculator className="text-blue-700" aria-hidden="true" />}
              eyebrow="Mantén presionado"
              title="Cantidad exacta"
              description="Mantén cualquier mitad presionada para abrir el teclado numérico y escribir la cantidad."
              tone="border-blue-100 bg-blue-50/70"
            />
          </div>

          <div className="rounded-2xl border border-emerald-200 bg-gradient-to-r from-emerald-50 to-cyan-50 p-4 sm:flex sm:items-center sm:justify-between sm:gap-5">
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-emerald-600 text-white shadow-sm">
                <FaWeightScale aria-hidden="true" />
              </span>
              <div>
                <div className="text-sm font-black text-emerald-950">Cuando el precio usa balanza</div>
                <p className="mt-1 text-xs leading-relaxed text-emerald-900/75 sm:text-sm">
                  La burbuja verde muestra la lectura estable que se confirmará al tocar la derecha. Si la balanza se desconecta, verás “Manual” y la tarjeta usará −1 / +1 normalmente; mantener presionado siempre abre el teclado.
                </p>
              </div>
            </div>
            <div className="mt-3 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-white px-4 py-2 text-sm font-black text-emerald-800 shadow-sm sm:mt-0">
              <span className="h-2 w-2 rounded-full bg-emerald-500" />
              1,235 kg
              <FiCheck aria-hidden="true" />
            </div>
          </div>

          {saveError && (
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800" role="alert">
              {saveError} Puedes volver a intentarlo o continuar; la guía podría aparecer nuevamente.
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-center text-xs text-gray-500 sm:text-left">
              Esta guía se muestra una sola vez para tu usuario.
            </p>
            <div className="flex gap-2">
              {saveError && (
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="flex-1 rounded-xl border border-gray-300 px-4 py-3 text-sm font-bold text-gray-700 hover:bg-gray-50 sm:flex-none"
                >
                  Continuar
                </button>
              )}
              <button
                type="button"
                onClick={handleAcknowledge}
                disabled={saving}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-blue-700 px-5 py-3 text-sm font-black text-white shadow-sm transition hover:bg-blue-800 disabled:cursor-wait disabled:bg-blue-400 sm:flex-none"
              >
                <FiCheck aria-hidden="true" />
                {saving ? 'Guardando…' : 'Entendido, comenzar'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PosFavGestureGuide;
